import { useState, useRef, useEffect } from 'react'
import { io, Socket } from 'socket.io-client'
import { api, BASE, ensureToken } from '../utils/api'
import { useEditorStore } from '../store/editor.store'

interface GeneratedSection {
  id: string
  anchorId?: string
  blocks: Array<{ id: string; type: string; data: Record<string, unknown> }>
}

interface GeneratedPageData {
  slug: string
  title: string
  isHome?: boolean
  seoTitle?: string
  seoDesc?: string
  sections: GeneratedSection[]
}

interface GeneratedPage {
  title: string
  seoTitle?: string
  seoDesc?: string
  pageCss?: string
  sections: GeneratedSection[]
  pages?: GeneratedPageData[]
}

interface DesignBriefTypography {
  displaySize?: string; h1Size?: string; h2Size?: string; h3Size?: string; h4Size?: string
  bodySize?: string; captionSize?: string; headingWeight?: string
  lineHeightHeading?: string; lineHeightBody?: string; letterSpacing?: string
}

interface DesignBriefRadius { sm?: string; md?: string; lg?: string; xl?: string }
interface DesignBriefShadows { sm?: string; md?: string; lg?: string }
interface DesignBriefButtons { radius?: string; height?: string; paddingX?: string }

interface DesignBrief {
  palette: {
    bg: string; bgAlt?: string; surface?: string
    accent: string; accentMuted?: string; accentLight?: string
    text?: string; textMuted?: string; border?: string
    success?: string; warning?: string; error?: string; light: string
  }
  fonts: { heading: string; body: string }
  typography?: DesignBriefTypography
  radius?: DesignBriefRadius
  shadows?: DesignBriefShadows
  buttons?: DesignBriefButtons
  voice?: string
}

interface SiteQualityScore {
  overallScore: number
  scores: { content: number; conversion: number; trustSignals: number; seo: number; completeness: number }
  verdict: string
  critiques: Array<{ severity: 'critical' | 'warning' | 'tip'; text: string }>
  improvements: string[]
  shouldImprove: boolean
}

interface UsageInfo {
  used: number
  limit: number
  plan: string
}

interface GuideOption {
  name: string
  cost: string
  difficulty: string
  time: string
  recommended?: boolean
}

interface GuideStep {
  title: string
  description: string
  options: GuideOption[]
}

interface GuideSection {
  title: string
  description: string
  steps: GuideStep[]
}

interface GuideEstimate {
  timeMin: string
  timeMax: string
  costSetup: string
  costMonthly: string
  difficulty: string
}

interface RoadmapPhase {
  phase: string
  tasks: string[]
}

interface ImplementationGuide {
  title: string
  summary: string
  sections: GuideSection[]
  totalEstimate: GuideEstimate
  roadmap: RoadmapPhase[]
  techStack: string[]
}

interface Props {
  onClose: () => void
}

const EXAMPLE_PROMPTS = [
  'Лендинг для студии йоги в Москве с расписанием, ценами и отзывами',
  'Сайт IT-агентства с портфолио, командой и формой заявки',
  'Магазин крафтового мыла ручной работы с описанием состава и доставки',
  'Landing page для мобильного приложения по медитации',
  'Ресторан с QR-меню на столиках и онлайн-заказом через Telegram',
]

const LANGUAGES = [
  { code: 'English', label: 'English (Английский)' },
  { code: 'Russian', label: 'Русский' },
  { code: 'German', label: 'Deutsch (Немецкий)' },
  { code: 'French', label: 'Français (Французский)' },
  { code: 'Spanish', label: 'Español (Испанский)' },
  { code: 'Chinese (Simplified)', label: '中文 (Китайский упрощённый)' },
  { code: 'Arabic', label: 'العربية (Арабский)' },
  { code: 'Portuguese', label: 'Português (Португальский)' },
  { code: 'Italian', label: 'Italiano (Итальянский)' },
  { code: 'Turkish', label: 'Türkçe (Турецкий)' },
  { code: 'Korean', label: '한국어 (Корейский)' },
  { code: 'Japanese', label: '日本語 (Японский)' },
]

type Tab = 'generate' | 'section' | 'translate'

// ─── Implementation Guide View ────────────────────────────────────────────────

const DIFF_COLORS: Record<string, { bg: string; text: string }> = {
  'легко':  { bg: '#d1fae5', text: '#065f46' },
  'средне': { bg: '#fef3c7', text: '#78350f' },
  'сложно': { bg: '#fee2e2', text: '#7f1d1d' },
}

function DiffBadge({ difficulty }: { difficulty: string }) {
  const c = DIFF_COLORS[difficulty] ?? { bg: '#f3f4f6', text: '#374151' }
  return (
    <span style={{ padding: '2px 7px', background: c.bg, color: c.text, borderRadius: '5px', fontSize: '11px', fontWeight: 700 }}>
      {difficulty}
    </span>
  )
}

function ImplementationGuideView({ guide }: { guide: ImplementationGuide }) {
  return (
    <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
      {/* Summary */}
      <p style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6, margin: '0 0 12px' }}>
        {guide.summary}
      </p>

      {/* Total estimate strip */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px', padding: '10px 12px', background: 'var(--bg-3)', borderRadius: '8px' }}>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>⏱ {guide.totalEstimate.timeMin}–{guide.totalEstimate.timeMax}</span>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', borderLeft: '1px solid var(--border)', paddingLeft: '6px' }}>💰 {guide.totalEstimate.costSetup}</span>
        {guide.totalEstimate.costMonthly && (
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', borderLeft: '1px solid var(--border)', paddingLeft: '6px' }}>📅 {guide.totalEstimate.costMonthly}</span>
        )}
        <span style={{ borderLeft: '1px solid var(--border)', paddingLeft: '6px' }}>
          <DiffBadge difficulty={guide.totalEstimate.difficulty} />
        </span>
      </div>

      {/* Sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {guide.sections.map((section, si) => (
          <div key={si}>
            <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '3px' }}>{section.title}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '10px', lineHeight: 1.5 }}>{section.description}</div>

            {section.steps.map((step, ti) => (
              <div key={ti} style={{ marginLeft: '8px', marginBottom: '10px' }}>
                <div style={{ fontWeight: 600, fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{step.title}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: '6px' }}>{step.description}</div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {step.options.map((opt, oi) => (
                    <div key={oi} style={{
                      padding: '8px 10px',
                      background: opt.recommended ? 'rgba(91,106,240,0.07)' : 'var(--bg-1)',
                      border: `1px solid ${opt.recommended ? 'rgba(91,106,240,0.25)' : 'var(--border)'}`,
                      borderRadius: '8px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>{opt.name}</span>
                        {opt.recommended && (
                          <span style={{ fontSize: '10px', fontWeight: 700, color: '#5b6af0', background: 'rgba(91,106,240,0.12)', padding: '1px 6px', borderRadius: '4px', flexShrink: 0 }}>★ рекомендую</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>💰 {opt.cost}</span>
                        <DiffBadge difficulty={opt.difficulty} />
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>⏱ {opt.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* Roadmap */}
        {guide.roadmap.length > 0 && (
          <div>
            <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>🗺 Роадмап</div>
            {guide.roadmap.map((phase, pi) => (
              <div key={pi} style={{ marginBottom: '8px', paddingLeft: '8px', borderLeft: '2px solid var(--accent)' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--accent)', marginBottom: '3px' }}>{phase.phase}</div>
                {phase.tasks.map((task, ti) => (
                  <div key={ti} style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.5 }}>• {task}</div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Tech stack */}
        {guide.techStack.length > 0 && (
          <div>
            <div style={{ fontWeight: 700, fontSize: '12px', marginBottom: '6px' }}>🛠 Стек</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {guide.techStack.map((tech, ti) => (
                <span key={ti} style={{ padding: '2px 8px', background: 'var(--bg-3)', borderRadius: '4px', fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 500 }}>{tech}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Quality Score View ───────────────────────────────────────────────────────

const SCORE_LABELS: Record<string, string> = {
  content: 'Тексты', conversion: 'Конверсия', trustSignals: 'Доверие', seo: 'SEO', completeness: 'Полнота',
}
const SEV_STYLE: Record<string, { bg: string; text: string; icon: string }> = {
  critical: { bg: '#fee2e2', text: '#991b1b', icon: '✕' },
  warning:  { bg: '#fef3c7', text: '#92400e', icon: '!' },
  tip:      { bg: '#d1fae5', text: '#065f46', icon: '✓' },
}

function ScoreBar({ label, value }: { label: string; value: number }) {
  const color = value >= 75 ? '#22c55e' : value >= 50 ? '#f59e0b' : '#ef4444'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
      <div style={{ width: '80px', fontSize: '11px', color: 'var(--text-muted)', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: '5px', background: 'var(--bg-3)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${value}%`, background: color, borderRadius: '3px', transition: 'width 0.5s ease' }} />
      </div>
      <div style={{ width: '28px', fontSize: '11px', fontWeight: 700, color, textAlign: 'right', flexShrink: 0 }}>{value}</div>
    </div>
  )
}

function QualityScoreView({ score }: { score: SiteQualityScore }) {
  const ringColor = score.overallScore >= 75 ? '#22c55e' : score.overallScore >= 50 ? '#f59e0b' : '#ef4444'
  const circumference = 2 * Math.PI * 22
  const dashOffset = circumference * (1 - score.overallScore / 100)
  return (
    <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
      {/* Overall score */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '14px' }}>
        <svg width="56" height="56" style={{ flexShrink: 0 }}>
          <circle cx="28" cy="28" r="22" fill="none" stroke="var(--bg-3)" strokeWidth="4" />
          <circle cx="28" cy="28" r="22" fill="none" stroke={ringColor} strokeWidth="4"
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            strokeLinecap="round" transform="rotate(-90 28 28)" style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
          <text x="28" y="33" textAnchor="middle" fontSize="14" fontWeight="700" fill={ringColor}>{score.overallScore}</text>
        </svg>
        <div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
            {score.overallScore >= 75 ? 'Хороший результат' : score.overallScore >= 50 ? 'Есть что улучшить' : 'Требует доработки'}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.5 }}>{score.verdict}</div>
        </div>
      </div>

      {/* Dimension scores */}
      <div style={{ marginBottom: '14px' }}>
        {(Object.entries(score.scores) as Array<[string, number]>).map(([k, v]) => (
          <ScoreBar key={k} label={SCORE_LABELS[k] ?? k} value={v} />
        ))}
      </div>

      {/* Critiques */}
      {score.critiques.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
          {score.critiques.map((c, i) => {
            const s = SEV_STYLE[c.severity] ?? SEV_STYLE.tip
            return (
              <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', padding: '6px 8px', background: s.bg, borderRadius: '6px' }}>
                <span style={{ fontSize: '11px', fontWeight: 700, color: s.text, flexShrink: 0, marginTop: '1px' }}>{s.icon}</span>
                <span style={{ fontSize: '11px', color: s.text, lineHeight: 1.4 }}>{c.text}</span>
              </div>
            )
          })}
        </div>
      )}

      {/* Improvements */}
      {score.improvements.length > 0 && (
        <div>
          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>Что улучшить</div>
          {score.improvements.map((imp, i) => (
            <div key={i} style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', gap: '5px', marginBottom: '3px' }}>
              <span style={{ color: '#5b6af0', flexShrink: 0 }}>→</span>
              <span>{imp}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Generate Tab ─────────────────────────────────────────────────────────────

function GenerateTab({ onClose }: { onClose: () => void }) {
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [preview, setPreview] = useState<GeneratedPage | null>(null)
  const [usage, setUsage] = useState<UsageInfo | null>(null)
  const [streamSections, setStreamSections] = useState<GeneratedSection[]>([])
  const [streamTitle, setStreamTitle] = useState('')
  const [streamProgress, setStreamProgress] = useState('')
  const [implementationGuide, setImplementationGuide] = useState<ImplementationGuide | null>(null)
  const [designBrief, setDesignBrief] = useState<DesignBrief | null>(null)
  const [qualityScore, setQualityScore] = useState<SiteQualityScore | null>(null)
  const [previewTab, setPreviewTab] = useState<'site' | 'plan' | 'quality'>('site')
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  // 'studio' — AI designs the whole page as one piece (qwen-draft level);
  // 'blocks' — classic assembly from editable blocks (design system v2).
  const [genMode, setGenMode] = useState<'studio' | 'blocks'>(() => {
    try { return localStorage.getItem('bst-ai-gen-mode') === 'blocks' ? 'blocks' : 'studio' } catch { return 'studio' }
  })
  const chooseMode = (m: 'studio' | 'blocks') => { setGenMode(m); try { localStorage.setItem('bst-ai-gen-mode', m) } catch { /* ignore */ } }
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const socketRef = useRef<Socket | null>(null)
  const { page, settings } = useEditorStore()

  useEffect(() => {
    api.get<UsageInfo>('/ai/usage').then(setUsage).catch(() => null)
  }, [])

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      socketRef.current?.disconnect()
      socketRef.current = null
    }
  }, [])

  function processChunk(
    obj: { _t: string; [k: string]: unknown },
    state: {
      sections: GeneratedSection[]
      pages: GeneratedPageData[]
      currentPage: GeneratedPageData | null
      meta: { title: string; seoTitle?: string; seoDesc?: string; pageCss?: string }
    },
  ): { preview?: GeneratedPage; error?: string } | null {
    if (obj._t === 'design_brief') {
      setDesignBrief(obj as unknown as DesignBrief)
    } else if (obj._t === 'meta') {
      state.meta = { title: obj.title as string, seoTitle: obj.seoTitle as string | undefined, seoDesc: obj.seoDesc as string | undefined, pageCss: obj.pageCss as string | undefined }
      setStreamTitle(obj.title as string)
    } else if (obj._t === 'page_start') {
      state.currentPage = {
        slug: obj.slug as string,
        title: obj.title as string,
        isHome: (obj.isHome as boolean) ?? false,
        seoTitle: obj.seoTitle as string | undefined,
        seoDesc: obj.seoDesc as string | undefined,
        sections: [],
      }
      setStreamTitle(obj.title as string)
      setStreamProgress(`Страница "${obj.title as string}"...`)
    } else if (obj._t === 'page_end') {
      if (state.currentPage) {
        state.pages.push(state.currentPage)
        setStreamSections([...state.pages.flatMap(p => p.sections)])
        setStreamProgress(`✓ Страница ${state.pages.length} готова`)
        state.currentPage = null
      }
    } else if (obj._t === 'section') {
      const sec = obj as unknown as GeneratedSection
      if (state.currentPage) {
        state.currentPage.sections.push(sec)
        setStreamSections([...state.pages.flatMap(p => p.sections), ...state.currentPage.sections])
      } else {
        state.sections.push(sec)
        setStreamSections([...state.sections])
      }
      const totalSecs = state.sections.length + state.pages.reduce((n, p) => n + p.sections.length, 0) + (state.currentPage?.sections.length ?? 0)
      setStreamProgress(`Секция ${totalSecs}...`)
    } else if (obj._t === 'progress') {
      setStreamProgress(obj.message as string)
    } else if (obj._t === 'implementation_guide') {
      setImplementationGuide(obj as unknown as ImplementationGuide)
    } else if (obj._t === 'quality_score') {
      setQualityScore(obj as unknown as SiteQualityScore)
    } else if (obj._t === 'error') {
      return { error: (obj.message as string) ?? 'Ошибка генерации' }
    } else if (obj._t === 'done') {
      if (state.pages.length > 0) {
        return { preview: { title: state.pages[0]?.title ?? '', sections: [], pages: state.pages } }
      } else if (state.sections.length > 0) {
        return { preview: { ...state.meta, sections: state.sections } }
      } else {
        return { error: 'AI не вернул секций — попробуйте ещё раз или упростите запрос' }
      }
    }
    return null
  }

  async function generate() {
    const trimmed = prompt.trim()
    if (!trimmed || trimmed.length < 10) {
      setError('Опишите сайт подробнее (минимум 10 символов)')
      return
    }
    setLoading(true)
    setError('')
    setPreview(null)
    setStreamSections([])
    setStreamTitle('')
    setStreamProgress('Запускаю генерацию...')
    setImplementationGuide(null)
    setDesignBrief(null)
    setQualityScore(null)
    setPreviewTab('site')

    const token = await ensureToken()
    const style = settings.designStyle || undefined

    try {
      // Step 1: Enqueue async job — returns immediately with jobId
      const jobRes = await fetch(`${BASE}/ai/job/generate-site`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt: trimmed, style, mode: genMode }),
      })
      if (!jobRes.ok) {
        const errText = await jobRes.text().catch(() => `HTTP ${jobRes.status}`)
        throw new Error(errText)
      }
      const { jobId } = await jobRes.json() as { jobId: string }
      setActiveJobId(jobId)
      setStreamProgress('Задача в очереди...')

      // Step 2: Connect WebSocket and listen for progress/done
      await new Promise<void>((resolve, reject) => {
        const wsBase = BASE.replace(/^http/, 'ws').replace(/\/api$/, '')
        const socket: Socket = io(`${wsBase}/ai-jobs`, {
          auth: { token },
          transports: ['websocket'],
          reconnectionAttempts: 5,
          timeout: 150_000,
        })
        socketRef.current = socket

        const state = {
          sections: [] as GeneratedSection[],
          pages: [] as GeneratedPageData[],
          currentPage: null as GeneratedPageData | null,
          meta: { title: '' } as { title: string; seoTitle?: string; seoDesc?: string; pageCss?: string },
        }

        socket.on('connect', () => {
          setStreamProgress('Подключено к серверу...')
          socket.emit('join-job', { jobId })
        })

        socket.on('job:progress', (data: { jobId: string; message: string; percent: number }) => {
          if (data.jobId !== jobId) return
          setStreamProgress(`${data.message} (${data.percent}%)`)
        })

        socket.on('job:done', (data: { jobId: string; ok: boolean; data?: unknown[]; error?: string }) => {
          if (data.jobId !== jobId) return
          socket.disconnect()
          socketRef.current = null
          setActiveJobId(null)

          if (!data.ok) {
            reject(new Error(data.error ?? 'Генерация завершилась с ошибкой'))
            return
          }

          // Process all chunks from the job result
          for (const chunk of (data.data ?? [])) {
            const result = processChunk(chunk as { _t: string; [k: string]: unknown }, state)
            if (result?.error) { reject(new Error(result.error)); return }
            if (result?.preview) { setPreview(result.preview) }
          }

          // Finalize if no explicit done chunk processed
          if (state.pages.length > 0) setPreview(p => p ?? { title: state.pages[0]?.title ?? '', sections: [], pages: state.pages })
          else if (state.sections.length > 0) setPreview(p => p ?? { ...state.meta, sections: state.sections })
          else reject(new Error('AI не вернул секций — попробуйте ещё раз или упростите запрос'))
          resolve()
        })

        socket.on('connect_error', (_err: Error) => {
          // WebSocket failed. The generated content is only delivered via job:done —
          // there is no REST endpoint to fetch it after the fact.
          // Reject immediately so the user sees a clear error instead of a
          // 3-minute polling wait that returns empty sections.
          socket.disconnect()
          socketRef.current = null
          reject(new Error('Не удалось подключиться к серверу. Проверьте соединение и попробуйте снова.'))
        })

        socket.on('disconnect', () => {
          socketRef.current = null
        })
      })

      api.get<UsageInfo>('/ai/usage').then(setUsage).catch(() => null)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
      setStreamProgress('')
      setActiveJobId(null)
    }
  }

  async function cancelGeneration() {
    if (!activeJobId) return
    const token = await ensureToken()
    await fetch(`${BASE}/ai/job/${activeJobId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => null)
    socketRef.current?.disconnect()
    socketRef.current = null
    setActiveJobId(null)
    setLoading(false)
    setStreamProgress('')
    setError('Генерация отменена')
  }

  function applySections(store: ReturnType<typeof useEditorStore.getState>, sections: GeneratedSection[]) {
    for (const sec of sections) {
      store.addSection()
      const added = useEditorStore.getState().page.sections.at(-1)
      if (added) {
        if (sec.anchorId) store.updateSection(added.id, { anchorId: sec.anchorId })
        for (const block of sec.blocks) store.addBlock(added.id, block.type, block.data)
      }
    }
  }

  function normalizeSlug(slug: string | undefined): string | null {
    if (!slug) return null
    if (slug === 'index') return '/'
    return slug.startsWith('/') ? slug : `/${slug}`
  }

  function applyToPage() {
    if (!preview) return
    const store = useEditorStore.getState()

    // Apply AI-generated design system to site theme
    if (designBrief) {
      const tc = {
        primaryColor: designBrief.palette.accent,
        bgColor: designBrief.palette.bg,
        // palette.text is the body text colour. palette.light is "light ink" — on a
        // light site it is near-white, which made every heading invisible.
        textColor: designBrief.palette.text ?? designBrief.palette.light,
        fontHeading: designBrief.fonts.heading,
        fontBody: designBrief.fonts.body,
        // Extended design system tokens
        surface: designBrief.palette.surface,
        bgAlt: designBrief.palette.bgAlt,
        accentLight: designBrief.palette.accentLight,
        buttons: designBrief.buttons,
        accentMuted: designBrief.palette.accentMuted,
        textMuted: designBrief.palette.textMuted,
        border: designBrief.palette.border,
        radius: designBrief.radius,
        shadows: designBrief.shadows,
        typography: designBrief.typography,
        // Core blocks are rendered from these tokens (packages/blocks/src/render/coreBlocks.ts)
        designSystem: 'v2',
      }
      store.updateSettings({ themeConfig: JSON.stringify(tc) })
    }

    if (preview.pages && preview.pages.length > 1) {
      for (const sec of [...page.sections]) store.removeSection(sec.id)

      const homePages = preview.pages.filter(p => p.isHome)
      const otherPages = preview.pages.filter(p => !p.isHome)

      const homePage = homePages[0] ?? preview.pages[0]
      const homeSlug = normalizeSlug(homePage.slug)
      store.updatePage({
        title: homePage.title,
        ...(homeSlug !== null && { slug: homeSlug }),
        seoTitle: homePage.seoTitle,
        seoDesc: homePage.seoDesc,
        // Apply full design system CSS to home page
        ...(preview.pageCss ? { pageCss: preview.pageCss } : {}),
      })
      applySections(store, homePage.sections)

      for (const pg of otherPages) {
        const aiSlug = normalizeSlug(pg.slug)
        store.addPage(pg.title)
        const newPageId = useEditorStore.getState().pages.at(-1)?.id
        if (newPageId) {
          store.setActivePage(newPageId)
          useEditorStore.getState().updatePage({
            title: pg.title,
            ...(aiSlug !== null && { slug: aiSlug }),
            seoTitle: pg.seoTitle,
            seoDesc: pg.seoDesc,
            // Propagate design system CSS to all pages for consistent fonts/tokens
            ...(preview.pageCss ? { pageCss: preview.pageCss } : {}),
          })
          applySections(useEditorStore.getState(), pg.sections)
        }
      }
    } else {
      store.updatePage({ title: preview.title, seoTitle: preview.seoTitle, seoDesc: preview.seoDesc, ...(preview.pageCss ? { pageCss: preview.pageCss } : {}) })
      for (const sec of [...page.sections]) store.removeSection(sec.id)
      applySections(store, preview.sections)
    }

    onClose()
  }

  function reset() {
    setPreview(null)
    setPrompt('')
    setDesignBrief(null)
    setImplementationGuide(null)
    setQualityScore(null)
    setPreviewTab('site')
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
          Опишите ваш сайт
        </label>
        <textarea
          ref={textareaRef}
          value={prompt}
          onChange={e => { setPrompt(e.target.value); setError('') }}
          placeholder="Например: Ресторан с QR-меню на столиках — клиент сканирует код и делает заказ прямо с телефона, повар получает уведомление в Telegram"
          rows={5}
          maxLength={6000}
          style={{ width: '100%', padding: '12px 14px', background: 'var(--bg-2)', border: `1px solid ${error ? '#ef4444' : 'var(--border)'}`, borderRadius: '12px', fontSize: '14px', color: 'var(--text-primary)', resize: 'vertical', lineHeight: 1.6, boxSizing: 'border-box', outline: 'none' }}
          onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') generate() }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
          {error && <div style={{ fontSize: '12px', color: '#ef4444' }}>{error}</div>}
          <div style={{ fontSize: '11px', color: prompt.length > 5500 ? '#ef4444' : 'var(--text-muted)', marginLeft: 'auto' }}>{prompt.length}/6000</div>
        </div>
        <div style={{ marginTop: '8px', padding: '8px 10px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: '8px', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          ⚠️ Запрос обрабатывается AI-провайдером (Anthropic, США; резерв — DeepSeek, КНР). Не вводите пароли, платёжные данные, а также персональные данные третьих лиц без их согласия.
        </div>
      </div>

      {!preview && !loading && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Режим генерации</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {([
              ['studio', '✦ Студия', 'ИИ проектирует цельный дизайн страницы. Тексты и фото меняются кликом на холсте.'],
              ['blocks', '▦ Блоки', 'Сайт из блоков в дизайн-системе ИИ. Можно двигать, добавлять и удалять блоки.'],
            ] as const).map(([m, label, hint]) => (
              <button key={m} type="button" onClick={() => chooseMode(m)}
                style={{ textAlign: 'left', padding: '10px 12px', borderRadius: '10px', cursor: 'pointer', background: genMode === m ? 'rgba(91,106,240,0.10)' : 'var(--bg-2)', border: `1px solid ${genMode === m ? '#5b6af0' : 'var(--border)'}` }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>{label}</div>
                <div style={{ fontSize: '11px', lineHeight: 1.45, color: 'var(--text-muted)' }}>{hint}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {!preview && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Примеры</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {EXAMPLE_PROMPTS.map((ex, i) => (
              <button key={i} onClick={() => { setPrompt(ex); textareaRef.current?.focus() }}
                style={{ padding: '5px 10px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '20px', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer', textAlign: 'left' }}>
                {ex}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && streamSections.length === 0 && (
        <div style={{ textAlign: 'center', padding: '32px 16px' }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: '3px solid var(--border)', borderTopColor: '#5b6af0', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '6px' }}>Создаю сайт...</div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '12px' }}>{streamProgress || 'AI строит структуру и тексты'}</div>
          {activeJobId && (
            <button onClick={cancelGeneration} style={{ padding: '6px 16px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', color: 'var(--text-muted)', cursor: 'pointer' }}>
              Отменить
            </button>
          )}
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      {loading && streamSections.length > 0 && (
        <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#5b6af0', animation: 'pulse 1s ease-in-out infinite', flexShrink: 0 }} />
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>
              {streamTitle ? `"${streamTitle}"` : 'Создаю...'}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '200px', overflowY: 'auto' }}>
            {streamSections.map((sec, i) => (
              <div key={i} style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: '#22c55e', flexShrink: 0 }}>✓</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{sec.anchorId ?? `sec-${i + 1}`}</span>
                <span>— {sec.blocks.length} блоков</span>
              </div>
            ))}
            {streamProgress && (
              <div style={{ fontSize: '12px', color: '#5b6af0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span>
                <span>{streamProgress}</span>
              </div>
            )}
          </div>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
        </div>
      )}

      {preview && !loading && (
        <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>Готово!</div>
          </div>

          {/* Tab switcher — only when extra tabs exist */}
          {(implementationGuide || qualityScore) && (
            <div style={{ display: 'flex', background: 'var(--bg-3)', borderRadius: '8px', padding: '3px', marginBottom: '12px' }}>
              <button
                onClick={() => setPreviewTab('site')}
                style={{ flex: 1, padding: '6px 10px', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, background: previewTab === 'site' ? 'var(--bg-1)' : 'transparent', color: previewTab === 'site' ? 'var(--text-primary)' : 'var(--text-muted)', cursor: 'pointer', transition: 'background 0.15s' }}
              >
                🌐 Сайт
              </button>
              {qualityScore && (
                <button
                  onClick={() => setPreviewTab('quality')}
                  style={{ flex: 1, padding: '6px 10px', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, background: previewTab === 'quality' ? 'var(--bg-1)' : 'transparent', color: previewTab === 'quality' ? (qualityScore.overallScore >= 75 ? '#22c55e' : qualityScore.overallScore >= 50 ? '#f59e0b' : '#ef4444') : 'var(--text-muted)', cursor: 'pointer', transition: 'background 0.15s', position: 'relative' }}
                >
                  📊 Качество · {qualityScore.overallScore}
                </button>
              )}
              {implementationGuide && (
                <button
                  onClick={() => setPreviewTab('plan')}
                  style={{ flex: 1, padding: '6px 10px', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, background: previewTab === 'plan' ? 'var(--bg-1)' : 'transparent', color: previewTab === 'plan' ? '#5b6af0' : 'var(--text-muted)', cursor: 'pointer', transition: 'background 0.15s' }}
                >
                  🔧 Реализация
                </button>
              )}
            </div>
          )}

          {/* Site info tab */}
          {previewTab === 'site' && (
            <>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                <b style={{ color: 'var(--text-primary)' }}>{preview.title}</b>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                {preview.pages && preview.pages.length > 1
                  ? `${preview.pages.length} страниц · ${preview.pages.reduce((n, p) => n + p.sections.length, 0)} секций · ${preview.pages.reduce((n, p) => n + p.sections.reduce((m, s) => m + s.blocks.length, 0), 0)} блоков`
                  : `${preview.sections.length} секций · ${preview.sections.reduce((n, s) => n + s.blocks.length, 0)} блоков`}
              </div>
              {preview.seoDesc && (
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic', borderTop: '1px solid var(--border)', paddingTop: '8px', marginTop: '8px' }}>{preview.seoDesc}</div>
              )}
              {implementationGuide && (
                <div style={{ marginTop: '10px', padding: '8px 10px', background: 'rgba(91,106,240,0.07)', border: '1px solid rgba(91,106,240,0.2)', borderRadius: '8px', fontSize: '12px', color: '#5b6af0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>🔧</span>
                  <span>AI подготовил план технической реализации — смотри вкладку «Реализация»</span>
                </div>
              )}
              {qualityScore && (
                <div onClick={() => setPreviewTab('quality')} style={{ marginTop: '8px', padding: '8px 10px', background: qualityScore.shouldImprove ? 'rgba(245,158,11,0.08)' : 'rgba(34,197,94,0.08)', border: `1px solid ${qualityScore.shouldImprove ? 'rgba(245,158,11,0.25)' : 'rgba(34,197,94,0.25)'}`, borderRadius: '8px', fontSize: '12px', color: qualityScore.shouldImprove ? '#92400e' : '#065f46', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <span>📊</span>
                  <span>Оценка AI-критика: <b>{qualityScore.overallScore}/100</b> — {qualityScore.shouldImprove ? 'есть рекомендации' : 'хороший результат'}</span>
                  <span style={{ marginLeft: 'auto', opacity: 0.6 }}>→</span>
                </div>
              )}
              <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                <button onClick={applyToPage} style={{ flex: 1, padding: '9px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
                  ✓ Применить к странице
                </button>
                <button onClick={reset}
                  style={{ padding: '9px 14px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  Ещё раз
                </button>
              </div>
            </>
          )}

          {/* Quality score tab */}
          {previewTab === 'quality' && qualityScore && (
            <QualityScoreView score={qualityScore} />
          )}

          {/* Implementation guide tab */}
          {previewTab === 'plan' && implementationGuide && (
            <ImplementationGuideView guide={implementationGuide} />
          )}
        </div>
      )}

      {usage && (
        <div style={{ fontSize: '11px', color: usage.limit !== -1 && usage.used >= usage.limit ? '#ef4444' : 'var(--text-muted)', background: 'var(--bg-2)', borderRadius: '8px', padding: '6px 10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: usage.limit !== -1 && usage.used >= usage.limit ? '#ef4444' : '#22c55e', display: 'inline-block', flexShrink: 0 }} />
          {usage.limit === -1
            ? `Генерации: ${usage.used} (безлимит · ${usage.plan})`
            : `Генерации: ${usage.used} из ${usage.limit} · тариф ${usage.plan}`}
        </div>
      )}

      {!preview && !loading && (
        <div style={{ marginTop: 'auto', paddingTop: '16px', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button onClick={generate} disabled={loading || prompt.trim().length < 10 || (usage !== null && usage.limit !== -1 && usage.used >= usage.limit)}
            style={{ flex: 1, padding: '11px 20px', background: 'linear-gradient(135deg,#5b6af0,#8b5cf6)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: (prompt.trim().length < 10 || (usage !== null && usage.limit !== -1 && usage.used >= usage.limit)) ? 'not-allowed' : 'pointer', opacity: (prompt.trim().length < 10 || (usage !== null && usage.limit !== -1 && usage.used >= usage.limit)) ? 0.6 : 1 }}>
            ✨ Сгенерировать сайт
          </button>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'right', lineHeight: 1.4 }}>Ctrl+Enter<br />для старта</div>
        </div>
      )}
    </div>
  )
}

// ─── Section Tab ──────────────────────────────────────────────────────────────

const SECTION_EXAMPLES = [
  'FAQ для студии йоги — 6 частых вопросов',
  'Секция с ценами — 3 тарифа',
  'Блок с командой — 4 человека',
  'Отзывы клиентов в виде карточек',
  'CTA-блок с призывом записаться на демо',
  'Секция с преимуществами услуги',
  'Форма обратной связи с полями имя, email, телефон',
  'Блок статистики с 4 ключевыми цифрами',
]

function SectionTab({ onClose }: { onClose: () => void }) {
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [preview, setPreview] = useState<{ blocks: Array<{ id: string; type: string; data: Record<string, unknown> }> } | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { page, settings } = useEditorStore()

  function getPageContext() {
    const blockTypes = page.sections.flatMap(s => s.blocks.map(b => b.type)).filter((v, i, a) => a.indexOf(v) === i).slice(0, 8)
    return `Page: "${page.title}". Existing blocks: ${blockTypes.join(', ')}.`
  }

  async function generate() {
    const trimmed = prompt.trim()
    if (!trimmed || trimmed.length < 5) {
      setError('Опишите секцию подробнее (минимум 5 символов)')
      return
    }
    setLoading(true)
    setError('')
    setPreview(null)
    try {
      const result = await api.post<{ blocks: Array<{ id: string; type: string; data: Record<string, unknown> }> }>('/ai/generate-section', {
        prompt: trimmed,
        pageContext: getPageContext(),
        style: settings.designStyle || undefined,
      })
      setPreview(result)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }

  function insertSection() {
    if (!preview) return
    const store = useEditorStore.getState()
    store.addSection()
    const added = useEditorStore.getState().page.sections.at(-1)
    if (added) {
      for (const block of preview.blocks) {
        store.addBlock(added.id, block.type, block.data)
      }
    }
    onClose()
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
          Опишите нужную секцию
        </label>
        <textarea
          ref={textareaRef}
          value={prompt}
          onChange={e => { setPrompt(e.target.value); setError('') }}
          placeholder="Например: FAQ с 6 вопросами о доставке и оплате для интернет-магазина"
          rows={3}
          style={{ width: '100%', padding: '12px 14px', background: 'var(--bg-2)', border: `1px solid ${error ? '#ef4444' : 'var(--border)'}`, borderRadius: '12px', fontSize: '14px', color: 'var(--text-primary)', resize: 'vertical', lineHeight: 1.6, boxSizing: 'border-box', outline: 'none' }}
          onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') generate() }}
        />
        {error && <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '6px' }}>{error}</div>}
      </div>

      {!preview && !loading && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>Примеры</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {SECTION_EXAMPLES.map((ex, i) => (
              <button key={i} onClick={() => { setPrompt(ex); textareaRef.current?.focus() }}
                style={{ padding: '5px 10px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '20px', fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer', textAlign: 'left' }}>
                {ex}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '28px 16px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', border: '3px solid var(--border)', borderTopColor: '#5b6af0', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>Генерируем секцию...</div>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      {preview && !loading && (
        <div style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>Готово!</div>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
            {preview.blocks.length} блоков: {preview.blocks.map(b => b.type).join(', ')}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={insertSection}
              style={{ flex: 1, padding: '9px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
              ✓ Добавить на страницу
            </button>
            <button onClick={() => { setPreview(null); setPrompt('') }}
              style={{ padding: '9px 14px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', color: 'var(--text-muted)', cursor: 'pointer' }}>
              Ещё раз
            </button>
          </div>
        </div>
      )}

      {!preview && !loading && (
        <div style={{ marginTop: 'auto', paddingTop: '12px', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button onClick={generate} disabled={prompt.trim().length < 5}
            style={{ flex: 1, padding: '11px 20px', background: 'linear-gradient(135deg,#5b6af0,#8b5cf6)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: prompt.trim().length < 5 ? 'not-allowed' : 'pointer', opacity: prompt.trim().length < 5 ? 0.6 : 1 }}>
            ⚡ Сгенерировать секцию
          </button>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'right', lineHeight: 1.4 }}>Ctrl+Enter<br />для старта</div>
        </div>
      )}
    </div>
  )
}

// ─── Translate Tab ────────────────────────────────────────────────────────────

function TranslateTab({ onClose }: { onClose: () => void }) {
  const [lang, setLang] = useState('English')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { page } = useEditorStore()

  async function translate() {
    setLoading(true)
    setError('')
    try {
      const content = { sections: page.sections }
      const result = await api.post<{ sections: unknown[] }>('/ai/translate', { targetLang: lang, content })
      const store = useEditorStore.getState()

      const translated = result.sections as Array<{
        id: string
        blocks: Array<{ id: string; type: string; data: Record<string, unknown> }>
        paddingTop?: number; paddingBottom?: number; background?: string
      }>

      for (const sec of [...page.sections]) store.removeSection(sec.id)
      for (const sec of translated) {
        store.addSection()
        const added = useEditorStore.getState().page.sections.at(-1)
        if (added) {
          for (const block of sec.blocks ?? []) {
            store.addBlock(added.id, block.type, block.data)
          }
        }
      }

      onClose()
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }

  const blockCount = page.sections.reduce((n, s) => n + s.blocks.length, 0)

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ background: 'var(--bg-2)', borderRadius: '12px', padding: '16px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>Текущая страница</div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          {page.title} · {page.sections.length} секций · {blockCount} блоков
        </div>
      </div>

      <div>
        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
          Перевести на язык
        </label>
        <select
          className="prop-input"
          value={lang}
          onChange={e => setLang(e.target.value)}
          style={{ fontSize: '14px', padding: '10px 12px' }}
        >
          {LANGUAGES.map(l => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
      </div>

      <div style={{ background: '#fff8e1', border: '1px solid #fde68a', borderRadius: '10px', padding: '12px 14px', fontSize: '12px', color: '#78350f', lineHeight: 1.6 }}>
        <b>Внимание:</b> Перевод заменит все текстовые блоки текущей страницы. Это действие можно отменить через Ctrl+Z.
      </div>

      {error && <div style={{ fontSize: '12px', color: '#ef4444', padding: '8px 12px', background: '#fef2f2', borderRadius: '8px' }}>{error}</div>}

      {loading && (
        <div style={{ textAlign: 'center', padding: '24px 16px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', border: '3px solid var(--border)', borderTopColor: '#5b6af0', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>Переводим страницу на {lang}...</div>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      {!loading && (
        <button onClick={translate}
          style={{ padding: '12px', background: 'linear-gradient(135deg,#5b6af0,#8b5cf6)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
          🌐 Перевести страницу на {lang}
        </button>
      )}
    </div>
  )
}

// ─── Modal Shell ──────────────────────────────────────────────────────────────

export function AiGenerateModal({ onClose }: Props) {
  const [tab, setTab] = useState<Tab>('generate')

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: '20px', width: '680px', maxWidth: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.4)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 0', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'linear-gradient(135deg,#5b6af0,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>✨</div>
              <div style={{ fontSize: '17px', fontWeight: 800, color: 'var(--text-primary)' }}>AI Ассистент</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', color: 'var(--text-muted)', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
          </div>
          <div style={{ display: 'flex', gap: '0' }}>
            {([['generate', '✨ Сайт'], ['section', '⚡ Секция'], ['translate', '🌐 Перевод']] as [Tab, string][]).map(([t, label]) => (
              <button key={t} onClick={() => setTab(t)}
                style={{ padding: '8px 16px', border: 'none', background: 'none', fontSize: '13px', fontWeight: 600, color: tab === t ? 'var(--accent)' : 'var(--text-muted)', borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent', cursor: 'pointer', marginBottom: '-1px' }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {tab === 'generate' ? <GenerateTab onClose={onClose} /> : tab === 'section' ? <SectionTab onClose={onClose} /> : <TranslateTab onClose={onClose} />}
      </div>
    </div>
  )
}
